/**
 * Provider-supplied customer reference carried from an inbound link into the CRM.
 *
 * The param only appears on the landing navigation, so it is buffered client-side until an
 * order is submitted — POST orders accepts `reference_id` and writes it to
 * Customer.ReferenceID. Mirrors src/config/reference.ts in oncesource-shopping-cart; keep
 * the two in step.
 */

/** Canonical spelling. Use this when generating links. */
export const REFERENCE_PARAM = 'reference_id';

/**
 * Accepted inbound parameter names, tested against the normalised key — lowercased with
 * separators stripped, see normaliseParamKey() in lib/captureReference.ts.
 *
 * Normalising collapses case and punctuation, so one pattern covers every such variant:
 * reference_id, ReferenceID, ReferenceId, Reference-Id, REFERENCE_ID and `reference id` all
 * normalise to 'referenceid'.
 *
 * What the pattern accepts, in every casing and separator style:
 *   reference, reference_id, reference_num, reference_number, reference_no
 *   ref_id, ref_num, ref_number, ref_no
 *   ...each with an optional customer_ prefix (customer_reference_id, customer_ref_num, …)
 *
 * Bare `ref` is deliberately excluded: in the wild it is overwhelmingly a traffic-source or
 * affiliate param (?ref=newsletter), and writing one of those into Customer.ReferenceID is
 * worse than capturing nothing.
 *
 * Providers build these links by hand, so being forgiving here costs nothing and saves a
 * support round-trip. The CRM's lead-create already accepts ReferenceNum and ReferenceNumber
 * alongside ReferenceID, so those spellings should be expected in inbound links too.
 */
export const REFERENCE_PARAM_PATTERN =
  /^(?:customer)?(?:reference(?:id|num|number|no)?|ref(?:id|num|number|no))$/;

/** Suffixed with the partner slug at runtime — see referenceStorageKey() in referenceStore.ts. */
export const REFERENCE_STORAGE_PREFIX = 'osc.reference';

/**
 * Attribution window. Bounds how long a stored reference can sit before it mis-credits an
 * unrelated later order, which matters most on shared and family devices.
 */
export const REFERENCE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Provisional. Customer.ReferenceID is varchar(255); 64 is a deliberately tighter bound
 * until the providers' real id format is known.
 */
export const REFERENCE_PATTERN = /^[A-Za-z0-9._:-]{1,64}$/;
